// 问卷API服务
// 根据提供的API文档实现问卷相关的API调用

import { getConfigAsync } from '../utils/config';
import { logger } from '../utils/logger';

// 动态获取 API_BASE_URL
async function getApiBaseUrl(): Promise<string> {
  const config = await getConfigAsync();
  return process.env.REACT_APP_QUESTIONNAIRE_API_URL || config.API_BASE_URL || 'https://getaws.alpha-pilot.net';
}

export interface QuestionnaireStatus {
  uuid: string;
  is_completed: boolean;
  onboarding_completed: boolean;
  completed_at?: string;
  message: string;
}

export interface QuestionnaireData {
  uuid: string;
  age: string;
  gender: string;
  trading_experience: string;
  watch_frequency: string;
}

export interface QuestionnaireSubmitResponse {
  success: boolean;
  uuid: string;
  message: string;
  error?: string;
  data: {
    age: string;
    gender: string;
    trading_experience: string;
    watch_frequency: string;
    is_completed: boolean;
    completed_at: string;
  };
}

export interface QuestionnaireGetResponse {
  success: boolean;
  data: {
    uuid: string;
    age: string;
    gender: string;
    trading_experience: string;
    watch_frequency: string;
    is_completed: boolean;
    completed_at: string;
    created_at: string;
  };
}

export interface ApiError {
  error: string;
  message: string;
}

/**
 * 获取用户问卷状态
 * @param uuid 用户唯一标识符
 * @returns 问卷状态信息
 */
export const getQuestionnaireStatus = async (uuid: string): Promise<QuestionnaireStatus> => {
  try {
    const API_BASE_URL = await getApiBaseUrl();
    const { getApiHeadersWithoutContentType } = await import('../utils/apiHeaders');
    const response = await fetch(`${API_BASE_URL}/api/user_questionnaire/status/${uuid}`, {
      method: 'GET',
      headers: getApiHeadersWithoutContentType(),
    });

    if (!response.ok) {
      // 保存状态码以便后续检查
      const statusCode = response.status;
      let errorData: ApiError;
      try {
        errorData = await response.json();
      } catch {
        // 如果响应不是JSON，创建一个错误对象
        errorData = {
          error: 'Unknown',
          message: `HTTP error! status: ${statusCode}`
        };
      }
      const error = new Error(errorData.message || `HTTP error! status: ${statusCode}`);
      (error as any).status = statusCode;
      throw error;
    }

    const data: QuestionnaireStatus = await response.json();
    return data;
  } catch (error) {
    logger.error('获取问卷状态失败:', error);
    throw error;
  }
};

/**
 * 提交用户问卷
 * @param data 问卷数据
 * @returns 提交结果
 */
export const submitQuestionnaire = async (data: QuestionnaireData): Promise<QuestionnaireSubmitResponse> => {
  try {
    const API_BASE_URL = await getApiBaseUrl();
    const { getApiHeaders } = await import('../utils/apiHeaders');
    const response = await fetch(`${API_BASE_URL}/api/user_questionnaire/submit`, {
      method: 'POST',
      headers: getApiHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData: ApiError = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result: QuestionnaireSubmitResponse = await response.json();
    return result;
  } catch (error) {
    logger.error('提交问卷失败:', error);
    throw error;
  }
};

/**
 * 获取用户问卷数据
 * @param uuid 用户唯一标识符
 * @returns 完整的问卷数据
 */
export const getQuestionnaireData = async (uuid: string): Promise<QuestionnaireGetResponse> => {
  try {
    const API_BASE_URL = await getApiBaseUrl();
    const { getApiHeadersWithoutContentType } = await import('../utils/apiHeaders');
    const response = await fetch(`${API_BASE_URL}/api/user_questionnaire/get/${uuid}`, {
      method: 'GET',
      headers: getApiHeadersWithoutContentType(),
    });

    if (!response.ok) {
      const errorData: ApiError = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data: QuestionnaireGetResponse = await response.json();
    return data;
  } catch (error) {
    logger.error('获取问卷数据失败:', error);
    throw error;
  }
};

/**
 * 标记 Onboarding 已完成
 * @param uuid 用户唯一标识符
 * @returns 更新结果
 */
export const completeOnboarding = async (uuid: string): Promise<{ success: boolean; message: string }> => {
  try {
    const API_BASE_URL = await getApiBaseUrl();
    const { getApiHeaders } = await import('../utils/apiHeaders');
    const response = await fetch(`${API_BASE_URL}/api/user_questionnaire/complete_onboarding`, {
      method: 'POST',
      headers: getApiHeaders(),
      body: JSON.stringify({ uuid }),
    });

    if (!response.ok) {
      const errorData: ApiError = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    logger.error('标记 Onboarding 完成失败:', error);
    throw error;
  }
};

/**
 * 健康检查
 * @returns 服务健康状态
 */
export const checkQuestionnaireHealth = async (): Promise<{ status: string; service: string; dynamodb: string }> => {
  try {
    const API_BASE_URL = await getApiBaseUrl();
    const { getApiHeadersWithoutContentType } = await import('../utils/apiHeaders');
    const response = await fetch(`${API_BASE_URL}/api/user_questionnaire/health`, {
      method: 'GET',
      headers: getApiHeadersWithoutContentType(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    logger.error('健康检查失败:', error);
    throw error;
  }
};
